// Home — what is owed, overdue first, and estimates waiting for an answer.
// docs/CLIENT_PORTAL.md §4. A property-only contact sees the estimates alone.

import React, { useCallback, useEffect, useState } from 'react';
import { EmptyState, Notice, Pill, RecordList, SectionLabel, Skeleton, Stat } from '../../../ui/fw';
import { get, money, day } from '../portalApi';
import { hasBilling, invoiceState, estimateState, openInvoice, openEstimate } from '../portalShared';

export default function Home({ me, onUnauthorized }) {
  const billing = hasBilling(me);
  const [data, setData] = useState(null);
  const [estimates, setEstimates] = useState(null);
  const [error, setError] = useState(null);
  // Estimates come from every client the person can see; invoices only from
  // those with billing access. So "For" is needed on each list for its own
  // reason: billing at one client and estimates at another is two clients,
  // and one invoice source (second review of #724).
  const several = (me.clients || []).length > 1;
  const severalBilling = (me.clients || []).filter((c) => c.access === 'billing').length > 1;

  const load = useCallback(async () => {
    setError(null);
    try {
      if (billing) {
        const s = await get('/portal/summary');
        setData(s);
        setEstimates(s.estimates_waiting || []);
      } else {
        const e = await get('/portal/estimates');
        setEstimates((e.estimates || []).filter((x) => x.status === 'sent'));
      }
    } catch (e) {
      if (e.status === 401) onUnauthorized(); else setError(e.message);
    }
  }, [billing, onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  if (error) return <Notice tone="stop" title="Your account could not be loaded" data-note="load-failed">{error}</Notice>;
  if ((billing && !data) || !estimates) return <Skeleton lines={5} />;

  const invoiceColumns = [
    { key: 'invoice_number', label: 'Invoice', role: 'title' },
    ...(severalBilling ? [{ key: 'client_name', label: 'For', role: 'subtitle' }] : []),
    { key: 'state', label: 'Status', role: 'badge', render: (r) => { const s = invoiceState(r); return <Pill tone={s.tone}>{s.label}</Pill>; } },
    { key: 'balance', label: 'Balance', align: 'right', render: (r) => money(r.balance_cents) },
    { key: 'issued', label: 'Issued', render: (r) => day(r.issued_on) },
    { key: 'due', label: 'Due', render: (r) => day(r.due_on) },
  ];
  const estimateColumns = [
    { key: 'number', label: 'Estimate', role: 'title' },
    ...(several ? [{ key: 'client_name', label: 'For', role: 'subtitle' }] : []),
    { key: 'state', label: 'Status', role: 'badge', render: (r) => { const st = estimateState(r.status); return <Pill tone={st.tone}>{st.label}</Pill>; } },
    { key: 'total', label: 'Total', align: 'right', render: (r) => money(r.total_cents) },
    { key: 'sent', label: 'Sent', render: (r) => day(r.sent_on) },
    { key: 'expires', label: 'Good until', render: (r) => day(r.expires_on) },
  ];

  return (
    <div className="pt-screen" data-screen="portal-home">
      {billing ? (
        <>
          <div className="pt-stats">
            <Stat label="You owe" value={money(data.owed_cents)} tone={data.owed_cents > 0 ? undefined : 'ok'} data-figure="owed" />
            <Stat label="Overdue" value={money(data.overdue_cents)} tone={data.overdue_cents > 0 ? 'stop' : undefined} data-figure="overdue" />
          </div>
          <p className="pt-muted">{`As of ${day(data.as_of)}. Open an invoice to see it, pay it, or download it.`}</p>
          <SectionLabel>Open invoices</SectionLabel>
          {data.open.length ? (
            <RecordList columns={invoiceColumns} rows={data.open} onRowClick={openInvoice} caption="Open invoices" />
          ) : (
            <EmptyState icon="check" title="Nothing owed">Every invoice is paid. Thank you.</EmptyState>
          )}
        </>
      ) : null}
      <SectionLabel>Estimates waiting for your answer</SectionLabel>
      {estimates.length ? (
        <RecordList columns={estimateColumns} rows={estimates} onRowClick={openEstimate} caption="Estimates waiting for your answer" />
      ) : (
        <EmptyState icon="estimates" title="No estimates waiting">When we send you an estimate, it is here to accept or decline.</EmptyState>
      )}
    </div>
  );
}
